import { TGeneratedScript } from "./ProductStructureType";

const GeneratedScriptDetailsModal = ({
  selectedScript,
}: {
  selectedScript: TGeneratedScript | null;
}) => {
  if (!selectedScript) return null;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {selectedScript.title}
        </h2>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-lg border">
            <span className="text-sm text-gray-600">Script Types:</span>
            <span className="text-sm font-semibold text-gray-900">
              {selectedScript.script_type}
            </span>
          </div>

          <div className="bg-blue-50 rounded-lg py-1 px-3 border border-blue-200">
            <span className="text-blue-800 text-sm font-semibold">
              Duration: {selectedScript.duration}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Script Content */}
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 border">
            <h3 className="font-semibold text-gray-900 mb-2">Hook</h3>
            <p className="text-gray-700 leading-relaxed">
              "{selectedScript.hook}"
            </p>
          </div>

          <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">
              Voiceover Script
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {selectedScript.voiceover_script}
            </p>
          </div>
        </div>

        {/* Additional Details */}
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 border">
            <h3 className="font-semibold text-gray-900 mb-2">Visuals</h3>
            <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
              {selectedScript.main_content}
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">Call to Action</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              {selectedScript.call_to_action}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 border-t border-gray-300 py-10">
        <div className="bg-gray-50 rounded-lg p-4 border">
          <h3 className="font-semibold text-gray-900 mb-2">Target Tags</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="font-medium text-gray-600">Hierarchy:</span>{" "}
              <span className="text-gray-900">
                {selectedScript.target_tags.hierarchy}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Storyline:</span>{" "}
              <span className="text-gray-900">
                {selectedScript.target_tags.storyline}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Hook:</span>{" "}
              <span className="text-gray-900">
                {selectedScript.target_tags.hook}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-600">CTA:</span>{" "}
              <span className="text-gray-900">
                {selectedScript.target_tags.cta}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Actor:</span>{" "}
              <span className="text-gray-900">
                {selectedScript.target_tags.actor}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Brand & Product</h3>
          <div className="text-sm space-y-1">
            <div>
              <span className="font-medium text-gray-600">Brand:</span>{" "}
              <span className="text-gray-900">{selectedScript.brand}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Product:</span>{" "}
              <span className="text-gray-900">{selectedScript.product}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Country:</span>{" "}
              <span className="text-gray-900">{selectedScript.country}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Persona:</span>{" "}
              <span className="text-gray-900">
                {selectedScript.persona_name}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">
            Pain Points Addressed
          </h3>
          <ul className="text-gray-700 text-sm space-y-1">
            {selectedScript.pain_points_addressed.map((point, index) => (
              <li key={index} className="flex items-start leading-relaxed">
                <span className="text-gray-400 mr-2 mt-0.5">•</span>
                {point}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">
            Desires Fulfilled
          </h3>
          <ul className="text-gray-700 text-sm space-y-1">
            {selectedScript.desires_fulfilled.map((desire, index) => (
              <li key={index} className="flex items-start leading-relaxed">
                <span className="text-gray-400 mr-2 mt-0.5">•</span>
                {desire}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Habits Leveraged</h3>
          <ul className="text-gray-700 text-sm space-y-1">
            {selectedScript.habits_leveraged.map((habit, index) => (
              <li key={index} className="flex items-start leading-relaxed">
                <span className="text-gray-400 mr-2 mt-0.5">•</span>
                {habit}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GeneratedScriptDetailsModal;
